import type { VoucherResponse } from '@/api/model/voucherResponse';
import { ProviderCategory } from '@/api/model/providerCategory';
import type { VoucherSortOrder } from '@/components/vouchers/VoucherFilterControls';
import { Card, CardHeader } from '@/components/ui/card';
import { VoucherDetailsModal } from '@/components/vouchers/VoucherDetailsModal';
import { VoucherGridContainer } from '@/components/vouchers/VoucherGridContainer';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { VoucherFilterBar } from './VoucherFilterBar';

// Map lowercase URL params to uppercase enum values (for backwards compatibility)
const categoryMap: Record<string, string> = {
  [ProviderCategory.GASTRONOMIE]: ProviderCategory.GASTRONOMIE,
  [ProviderCategory.ERLEBEN]: ProviderCategory.ERLEBEN,
  [ProviderCategory.SHOPPING]: ProviderCategory.SHOPPING,
  [ProviderCategory.SERVICES]: ProviderCategory.SERVICES,
};

const normalizeCategory = (category: string | null): string => {
  if (!category) {
    return 'alle';
  }
  // Check if it's a valid enum value or mapped value
  const normalized = categoryMap[category.toLowerCase()] || category;
  // Validate it's a valid category
  if (normalized === 'alle' || Object.values(ProviderCategory).includes(normalized as ProviderCategory)) {
    return normalized;
  }
  return 'alle';
};

export const VoucherListPage: React.FC = () => {
  const [selectedVoucher, setSelectedVoucher] = useState<VoucherResponse | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const voucherListRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter states - initialize from URL params
  const [selectedCategory, setSelectedCategory] = useState(() => normalizeCategory(searchParams.get('category')));
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<VoucherSortOrder>('newest');

  // Sync URL params with state
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const normalized = normalizeCategory(categoryParam);
    setSelectedCategory(normalized);
  }, [searchParams]);

  const handleVoucherClick = (voucher: VoucherResponse) => {
    setSelectedVoucher(voucher);
    setIsModalOpen(true);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    // Update URL params
    if (category === 'alle') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ category }, { replace: true });
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleSortChange = (order: VoucherSortOrder) => {
    setSortOrder(order);
  };

  return (
    <div className='space-y-20'>
      {/* Layout Card wrapping filter bar, grid, and pagination */}
      <Card ref={voucherListRef} className='rounded-[16px] shadow-xs'>
        <CardHeader className='px-6 pt-6 pb-6 md:px-6 md:pt-8 lg:px-10 lg:pt-10'>
          <VoucherFilterBar
            selectedCategory={selectedCategory}
            searchQuery={searchQuery}
            onCategoryChange={handleCategoryChange}
            onSearchChange={handleSearchChange}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />
        </CardHeader>

        <VoucherGridContainer
          selectedCategory={selectedCategory}
          searchQuery={searchQuery}
          sortOrder={sortOrder}
          onVoucherClick={handleVoucherClick}
        />
      </Card>

      <VoucherDetailsModal voucher={selectedVoucher} open={isModalOpen} onOpenChange={setIsModalOpen} />
    </div>
  );
};
